import { Button, Checkbox, Dialog, DialogContent, DialogTitle, Divider, FormControlLabel } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import { distinct, except } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../../api/adminApi";
import type { WorkflowActivityOutputField } from "../../../../../api/types/workflowTypes";
import WorkflowTemplateDetailsContext from "../WorkflowTemplateDetailsContext";
import WorkflowTemplateActivityFieldForm from "./WorkflowTemplateActivityFieldForm";
import {
  type WorkflowTemplateActivityFieldFormState,
  validateWorkflowTemplateActivityFieldForm,
} from "./workflowTemplateActivityFieldFormState";

interface Props {
  activityId: string;
  field: WorkflowActivityOutputField;
  onClose: () => void;
}

const updateActivityInWorkflowTemplate = withErrorHandling(adminApi.updateActivityInWorkflowTemplate);

const isFormDirty = (
  field: WorkflowActivityOutputField,
  formState: WorkflowTemplateActivityFieldFormState
): boolean => {
  return (
    formState.displayName.trim() !== field.displayName.trim() ||
    formState.description.trim() !== field.description.trim() ||
    formState.isRequired !== field.attributes.includes("Required")
  );
};

const EditWorkflowTemplateActivityFieldDialog = ({ activityId, field, onClose }: Props) => {
  const { workflowTemplate, onActivityUpdated } = useDefinedContext(WorkflowTemplateDetailsContext);

  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [saving, setSaving] = useState(false);
  const [updateActiveWorkflows, setUpdateActiveWorkflows] = useState(false);

  const [formState, setFormState] = useState<WorkflowTemplateActivityFieldFormState>({
    displayName: field.displayName,
    type: field.type,
    description: field.description,
    isRequired: field.attributes.includes("Required"),
  });

  const descriptionChanged = formState.description.trim() !== field.description.trim();

  const handleSave = async () => {
    setSaving(true);
    const [updatedActivity, error] = await updateActivityInWorkflowTemplate(workflowTemplate.id, activityId, {
      outputFieldSchema: {
        updatedFields: [
          {
            id: field.id,
            displayName: field.isCustomField ? formState.displayName.trim() : undefined,
            descriptionUserInput: formState.description.trim(),
            attributes: formState.isRequired
              ? distinct(["Required", ...field.attributes])
              : except([...field.attributes], ["Required"]),
          },
        ],
      },
      updateActiveWorkflows: descriptionChanged && updateActiveWorkflows,
    });
    setSaving(false);

    if (error) {
      logError(error, "[EditWorkflowTemplateActivityFieldDialog] updateActivityInWorkflowTemplate");
      sendNotificationError(error.message);
      return;
    }

    onActivityUpdated(updatedActivity);
    sendNotification("Field updated successfully");
    onClose();
  };

  const validationResults = validateWorkflowTemplateActivityFieldForm(formState);

  const isDirty = isFormDirty(field, formState);

  return (
    <Dialog maxWidth="sm" fullWidth open onClose={onClose}>
      <DialogTitle>Edit Field</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />

      <DialogContent>
        <WorkflowTemplateActivityFieldForm
          formState={formState}
          validationResults={validationResults}
          disableDisplayName={!field.isCustomField}
          disableType
          onChange={setFormState}
        />
        {descriptionChanged && (
          <FormControlLabel
            sx={{ mt: 2 }}
            label="Also update this field's description in active workflows"
            disabled={saving}
            control={
              <Checkbox checked={updateActiveWorkflows} onChange={(_, checked) => setUpdateActiveWorkflows(checked)} />
            }
          />
        )}
      </DialogContent>

      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          loading={saving}
          disabled={!validationResults.isValid || !isDirty}
        >
          Save
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default EditWorkflowTemplateActivityFieldDialog;
